export type FilterPrimitive = string | number

export interface ValueCount<T extends FilterPrimitive = FilterPrimitive> {
  value: T
  count: number
}

export interface AssigneeCount {
  value: string
  count: number
  label?: string
  active?: boolean
}

export interface AssigneeFilterOptions {
  getLabel?: (id: string) => string
  isBlocked?: (id: string) => boolean
  unassignedLabel?: string
}

/**
 * Counts occurrences of each value in a single pass, skipping empty values and
 * any value listed in `excludedValues` (e.g. 'UNDEFINED_CATEGORY', -1, -10000).
 */
export function buildValueCounts<T extends FilterPrimitive>(values: Array<T | null | undefined>, excludedValues: FilterPrimitive[] = []): ValueCount<T>[] {
  const counts = new Map<T, number>()
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    if (value === null || value === undefined || value === '') {
      continue
    }
    if (excludedValues.includes(value)) {
      continue
    }
    counts.set(value, (counts.get(value) || 0) + 1)
  }
  const result: ValueCount<T>[] = []
  counts.forEach((count, value) => {
    result.push({ value, count })
  })
  return result
}

/**
 * Counts assignee ids in a single pass, bucketing falsy ids into an "unassigned"
 * entry (value ''), and optionally decorating each entry with a label/active flag.
 */
export function buildAssigneeFilterCounts(ids: Array<string | null | undefined>, options: AssigneeFilterOptions = {}): AssigneeCount[] {
  const { getLabel, isBlocked, unassignedLabel } = options
  const counts = new Map<string, number>()
  let unassigned = 0
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i]
    if (!id) {
      unassigned++
    } else {
      counts.set(id, (counts.get(id) || 0) + 1)
    }
  }
  const result: AssigneeCount[] = []
  counts.forEach((count, value) => {
    const entry: AssigneeCount = { count, value }
    if (getLabel) {
      entry.label = getLabel(value)
    }
    if (isBlocked) {
      entry.active = !isBlocked(value)
    }
    result.push(entry)
  })
  if (unassigned > 0) {
    const entry: AssigneeCount = { count: unassigned, value: '' }
    if (unassignedLabel !== undefined) {
      entry.label = unassignedLabel
    }
    if (isBlocked) {
      entry.active = true
    }
    result.push(entry)
  }
  return result
}

/**
 * Orders floor-count entries as: ground floor(s) (0), then ascending positive
 * floors, then descending negative floors (i.e. basement levels closest to
 * ground first).
 */
export function sortFloorCounts<T extends { value: number }>(items: T[]): T[] {
  if (!items) {
    return items
  }
  const posFloor: T[] = []
  const negFloor: T[] = []
  const floorZero: T[] = []
  for (const item of items) {
    const value = parseInt(String(item.value))
    if (value > 0) {
      posFloor.push(item)
    } else if (value < 0) {
      negFloor.push(item)
    } else if (value === 0) {
      floorZero.push(item)
    }
  }
  posFloor.sort((a, b) => parseInt(String(a.value)) - parseInt(String(b.value)))
  negFloor.sort((a, b) => parseInt(String(b.value)) - parseInt(String(a.value)))
  return floorZero.concat(posFloor).concat(negFloor)
}
